import { useLingui } from "@lingui/react/macro";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { CostUnavailableReason } from "@/lib/api/requests/recipes";

export function formatCurrency(value: number | null | undefined): string {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

interface CostValueProps {
  value: number | null | undefined;
  reason?: CostUnavailableReason | null;
  /** The API's specifics — which unit pair failed, which prep recipe. */
  detail?: string | null;
  className?: string;
}

/**
 * A costed number, or the reason there isn't one (T-605).
 *
 * A bare dash tells a cook nothing they can act on, and every one of T-604's
 * reasons has a different fix: price the ingredient, change the unit, give the
 * prep recipe a yield. So the explanation is carried on the element's
 * accessible name as well as in the tooltip — a chef reading the table with a
 * screen reader should not have to hover to find out why a cost is missing.
 *
 * `SUB_RECIPE_INCOMPLETE` is the odd one out: it arrives with a number, which
 * is a floor rather than the answer, so it renders as "≥ $4.20".
 */
export function CostValue({ value, reason, detail, className }: CostValueProps) {
  const { t } = useLingui();

  if (value == null && !reason) {
    return <span className={cn("text-sm text-(--color-text-secondary)", className)}>—</span>;
  }

  const explanations: Record<CostUnavailableReason, string> = {
    NO_UNIT_COST: t`No cost has been entered for this ingredient yet.`,
    UNCONVERTIBLE_UNIT: t`This line's unit can't be converted to the unit the ingredient is priced in. Add a pack size, or measure the line in a unit of the same kind.`,
    SUB_RECIPE_NO_YIELD: t`This prep recipe doesn't say how much a batch makes, so a share of it can't be priced.`,
    SUB_RECIPE_INCOMPLETE: t`Some ingredients of this prep recipe have no cost, so this is a minimum.`,
    RECIPE_CYCLE: t`This recipe ends up using itself through its prep recipes, so it can't be costed. Remove the loop.`,
  };

  const explanation = reason ? explanations[reason] : null;
  const isFloor = value != null;
  const shown = isFloor ? `≥ ${formatCurrency(value)}` : "—";
  const label = explanation
    ? isFloor
      ? t`At least ${formatCurrency(value)}. ${explanation}`
      : t`No cost. ${explanation}`
    : formatCurrency(value);

  if (!explanation) {
    return (
      <span className={cn("text-sm text-(--color-text-primary)", className)}>
        {formatCurrency(value)}
      </span>
    );
  }

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={label}
            className={cn(
              "cursor-help text-sm underline decoration-dotted underline-offset-4",
              isFloor ? "text-(--color-text-primary)" : "text-(--color-text-secondary)",
              className
            )}
          >
            {shown}
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-72 text-wrap">
          <p>{explanation}</p>
          {detail ? <p className="mt-1 opacity-80">{detail}</p> : null}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
